<script>
export default {
    name: 'IntervalSlider',

    props: {
        value: Object, // Should be of structure: { value: Number, unit: String }
        label: String, // Custom label built by parent component from the given value
        sections: Array,
        /* Array is composed of objects of the following form: 
        {
            interval: Number,
            unit: String,
            start: Number,      // Start and End should be evenly divisible by Interval
            end: Number,
            weight?: Number,    // int
            doNotMarkEnd?: Boolean,
        }
        */
    },

    data() {
        return {
            barWidth: 0, // Total width of the slider bar
            startDragLocationX: 0,
            dragTargetValue: { value: 0, unit: '' }, // The value cloest to the end of the drag location, confirmed in dialog
            sliderMoveTargetOffset: 0, // The offset in pixels of where the slider should want to transition to
            dragging: false,
        };
    },

    computed: {
        sliderLabel() {
            if (this.label) {
                return this.label;
            }

            if (this.value) {
                return `${this.value.value} ${this.value.unit}`;
            }

            return '--';
        },

        sliderStylingObject() {
            return {
                left: `${this.sliderMoveTargetOffset}px`,
            };
        },

        totalSectionWeights() {
            let weights = 0;
            for (const section of this.sections) {
                if (section.weight) {
                    weights += section.weight;
                } else {
                    weights++;
                }
            }

            return weights;
        },

        // Objects keyed on pixel offset from the left of the bar containing details for every bar value
        tickObjects() {
            const ticks = {};
            let offset = 0;
            for (const section of this.sections) {
                // Figure out how wide the section should be based on the provided weight and the width of the bar as a whole
                let weight = 1;
                if (section.weight) {
                    weight = section.weight;
                }

                const sectionWidth =
                    this.barWidth * (weight / this.totalSectionWeights);
                const tickWidth =
                    sectionWidth /
                    ((section.end - section.start) / section.interval + 1);

                for (
                    let i = section.start;
                    i <= section.end;
                    i += section.interval
                ) {
                    ticks[offset] = {
                        value: i,
                        unit: section.unit,
                    };

                    offset += tickWidth;
                }
            }

            return ticks;
        },
    },

    watch: {
        value: {
            immediate: false,
            handler: function (newVal, oldVal) {
                if (
                    !this.dragging &&
                    newVal &&
                    (!oldVal ||
                        newVal.value !== oldVal.value ||
                        newVal.unit !== oldVal.unit)
                ) {
                    this.setSliderToValue(newVal);
                }
            },
        },
    },

    mounted() {
        window.addEventListener('resize', this.onResizeWindow);

        this.$nextTick(() => {
            this.initializeSlider();
        });
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.onResizeWindow);
    },

    methods: {
        initializeSlider() {
            // recalc bar width
            if (this.$refs.bar) {
                this.barWidth = this.$refs.bar.clientWidth;
            }

            // wait a tick check value and resnap to value on bar
            this.$nextTick(() => {
                if (this.value) {
                    this.setSliderToValue(this.value);
                }
            });
        },

        computeClosestValueFromOffset(snapTo = false) {
            const closestTick = Object.keys(this.tickObjects).reduce((a, b) => {
                return Math.abs(b - this.sliderMoveTargetOffset) <
                    Math.abs(a - this.sliderMoveTargetOffset)
                    ? b
                    : a;
            });

            this.dragTargetValue = this.tickObjects[closestTick];

            if (snapTo) {
                this.sliderMoveTargetOffset = closestTick;
            }
        },

        onResizeWindow() {
            if (this.$refs.bar) {
                this.barWidth = this.$refs.bar.clientWidth;
                // re-snap to current value after resize
                this.setSliderToValue(this.value);
            }
        },

        onSliderMoveStart: function (event) {
            this.dragging = true;
            this.startDragLocationX =
                event.type === 'touchstart' && event.touches && event.touches[0]
                    ? event.touches[0].clientX
                    : event.clientX;
            window.addEventListener('mousemove', this.onSliderMove);
            window.addEventListener('touchmove', this.onSliderMove, {
                passive: true,
            });
            window.addEventListener('mouseup', this.onSliderMoveEnd);
            window.addEventListener('touchend', this.onSliderMoveEnd);
        },

        onSliderMove(event) {
            // Figure out the offset of the drag from the start of the bar to give the slider dot a movement target
            const left = this.$refs.bar.getBoundingClientRect().left;
            let eventX =
                event.type === 'touchmove' && event.touches && event.touches[0]
                    ? event.touches[0].clientX
                    : event.clientX;
            if (eventX < left) {
                eventX = left;
            } else if (eventX > left + this.barWidth) {
                eventX = left + this.barWidth;
            }
            this.sliderMoveTargetOffset = eventX - left;

            this.computeClosestValueFromOffset();
            this.$emit('change', this.dragTargetValue);
        },

        onSliderMoveEnd(event) {
            this.dragging = false;
            this.computeClosestValueFromOffset(true);
            window.removeEventListener('mousemove', this.onSliderMove);
            window.removeEventListener('touchmove', this.onSliderMove);
            window.removeEventListener('mouseup', this.onSliderMoveEnd);
            window.removeEventListener('touchend', this.onSliderMoveEnd);

            this.$emit('change', this.dragTargetValue);
            this.$emit('submit');
        },

        setSliderToValue(value) {
            for (const offset of Object.keys(this.tickObjects)) {
                const tick = this.tickObjects[offset];
                if (value.value === tick.value && value.unit === tick.unit) {
                    this.sliderMoveTargetOffset = offset;
                }
            }
        },
    },
};
</script>

<template>
    <div class="interval-slider">
        <div class="slider-bar" ref="bar">
            <div
                @mousedown="onSliderMoveStart"
                @touchstart.passive="onSliderMoveStart"
                :style="sliderStylingObject"
                class="slider-dot-block"
                ref="slider"
            >
                <div class="slider-dot" />
            </div>
            <div
                v-for="section in sections"
                :key="`${section.unit}:${section.start}-${section.end}`"
                :class="['bar-section', { 'no-edge': section.doNotMarkEnd }]"
                :style="{ 'flex-grow': section.weight ? section.weight : 1 }"
            >
                <div class="back-block" />
            </div>
        </div>
        <div class="slider-label">{{ sliderLabel }}</div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';
@use '../../../../palette.scss' as *;

.interval-slider {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    .slider-bar {
        position: relative;
        width: 100%;
        height: 30px;
        border-left: 2px solid $theme-light-blue;
        border-right: 2px solid $theme-light-blue;
        border-radius: 5px;
        display: flex;
        align-items: center;

        .slider-dot-block {
            position: absolute;
            height: 22px;
            width: 22px;
            touch-action: none;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            left: 0;
            top: 4px;
            transform: translateX(-50%);
            border: 1px dashed $theme-light-blue;

            &:hover {
                cursor: pointer;
            }

            .slider-dot {
                position: relative;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background-color: $theme-light-blue;
            }
        }

        .bar-section {
            height: 20px;
            flex-grow: 1;
            display: inline-flex;
            align-items: center;

            &:not(.no-edge) {
                border-right: 1px solid $theme-grey;
            }

            &:last-child {
                border-right: none;
            }

            .back-block {
                background-color: color.adjust($theme-light-grey, $alpha: -0.5);
                height: 12px;
                flex-grow: 1;
            }
        }
    }

    .slider-label {
        color: $theme-grey;
    }
}
</style>
